import { IconCopy } from '@developerz/ui';
import { createEffect, createSignal, type JSX, on, Show } from 'solid-js';
import { t } from '../i18n';
import { absolute } from '../lib/time';
import { Button } from './ui/Button';

// Reveal-once enrollment token (06b): the join one-liner no longer carries the
// token (it reads it at a prompt, keeping it out of shell history), so the
// operator pastes THIS value at the command's `Enrollment token:` prompt. The
// plaintext lives only in the parent modal's signal and dies on close; this
// presenter just renders it with its own copy button. Prop-driven, no fetch.
//
// The parent is repeatable (arc 101 slice 03 step 6 — four machines, four
// single-use tokens), so `token` CHANGES in place under a mounted instance.
// Two things follow, and both are load-bearing rather than cosmetic:
//   - `copied` resets on that change. A stale "Copied" is a lie about which
//     secret is on the clipboard, and believing it means pasting box 1's spent
//     token into box 2 — a failed join whose cause is invisible on screen.
//   - `ordinal` labels which token this is. The two tokens differ only in a
//     random suffix, so without a counter the screen is byte-identical before
//     and after a mint and the operator cannot tell the new one from the old.

export type EnrollTokenRevealProps = {
  readonly token: string;
  /**
   * 1-based position in this dialog's mint run. Absent or 1 renders the plain
   * label — the single-box path is the common one and a bare "1" there is noise
   * about a batch nobody started.
   */
  readonly ordinal?: number;
  /**
   * When this code stops working: the mint response's `expires_iso` (#6567),
   * the instant the store wrote. Absent (an older api) states no expiry rather
   * than a TTL guessed here.
   */
  readonly expiresIso?: string | undefined;
};

export function EnrollTokenReveal(props: EnrollTokenRevealProps): JSX.Element {
  const [copied, setCopied] = createSignal(false);
  // `defer` so this only ever answers a REPLACEMENT: on mount `copied` is
  // already false and re-asserting it would hide a genuine ordering bug.
  createEffect(
    on(
      () => props.token,
      () => setCopied(false),
      { defer: true },
    ),
  );
  const label = (): string => {
    const n = props.ordinal ?? 1;
    return n > 1 ? t('fleet.addbox.token_label_nth', { n }) : t('fleet.addbox.token_label');
  };
  // Guarded: happy-dom (tests) and non-secure contexts lack navigator.clipboard.
  const copy = (): void =>
    void navigator.clipboard?.writeText(props.token).then(
      () => setCopied(true),
      () => setCopied(false),
    );

  return (
    <div class="alert token-secret" role="status">
      <p class="alert__title">{label()}</p>
      <p class="alert__desc">{t('fleet.addbox.token_hint')}</p>
      <code class="code-block token-secret__value">{props.token}</code>
      <Show when={props.expiresIso}>
        {(iso) => (
          <p class="alert__desc">{t('fleet.connect.token_expires', { at: absolute(iso()) })}</p>
        )}
      </Show>
      <div class="token-secret__actions">
        <Button variant="outline" size="sm" onClick={copy}>
          <IconCopy size="0.875rem" />
          {t(copied() ? 'fleet.addbox.copied' : 'fleet.addbox.copy_token')}
        </Button>
      </div>
    </div>
  );
}
